"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { TotemWithLocation } from "@/app/(users)/totem-by-slug/[slug]/actions/get-totem-with-location";
import { useTotemCart } from "@/app/(users)/totem-by-slug/[slug]/hooks/use-totem-cart";
import { useTotemContext } from "@/app/(users)/totem-by-slug/[slug]/hooks/use-totem-context";
import { useTotemMenu } from "@/app/(users)/totem-by-slug/[slug]/hooks/use-totem-menu";
import type { MenuSnapshotProduct } from "@/app/(users)/totem-by-slug/[slug]/types/menu-snapshot";
import { sortCategories } from "@/app/(users)/totem-by-slug/[slug]/utils/category-products";
import { shouldShowProduct } from "@/app/(users)/totem-by-slug/[slug]/utils/product-stock";

export type FlyToCartPayload = {
  imageUrl: string;
  fromRect: DOMRect;
};

type TotemContextValue = {
  totem: TotemWithLocation | null | undefined;
  totemLoading: boolean;
  menu: ReturnType<typeof useTotemMenu>["menu"];
  menuLoading: boolean;
  categories: ReturnType<typeof sortCategories>;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string) => void;
  activeCategoryName: string;
  cart: ReturnType<typeof useTotemCart>;
  selectedProduct: MenuSnapshotProduct | null;
  setSelectedProduct: (product: MenuSnapshotProduct | null) => void;
  editingCartItemId: string | null;
  setEditingCartItemId: (id: string | null) => void;
  checkoutOpen: boolean;
  setCheckoutOpen: (open: boolean) => void;
  triggerFlyToCart: (payload: FlyToCartPayload) => void;
  flyAnimation: FlyToCartPayload | null;
  clearFlyAnimation: () => void;
  desktopCheckoutRef: React.RefObject<HTMLElement | null>;
  mobileCheckoutRef: React.RefObject<HTMLElement | null>;
  orderPanelOpen: boolean;
  setOrderPanelOpen: (open: boolean) => void;
  orderOptionsOpen: boolean;
  setOrderOptionsOpen: (open: boolean) => void;
  orderType: "takeaway" | "eat_here" | null;
  setOrderType: (type: "takeaway" | "eat_here" | null) => void;
  pickupMethod: "banderin" | "beeper" | "nombre" | null;
  setPickupMethod: (method: "banderin" | "beeper" | "nombre" | null) => void;
  beeperNumber: string;
  setBeeperNumber: (num: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
};

export const TotemCtx = createContext<TotemContextValue | null>(null);

export function useOptionalTotem() {
  return useContext(TotemCtx);
}

export function useTotem() {
  const ctx = useContext(TotemCtx);
  if (!ctx) {
    throw new Error("useTotem debe usarse dentro de TotemProvider");
  }
  return ctx;
}

type Props = {
  totemId: string;
  children: ReactNode;
};

export function TotemProvider({ totemId, children }: Props) {
  const { totem, isLoading: totemLoading } = useTotemContext(totemId);
  const { menu, isLoading: menuLoading } = useTotemMenu(
    totemId,
    totem?.active_menu_id,
  );
  const cart = useTotemCart();
  const desktopCheckoutRef = useRef<HTMLElement>(null);
  const mobileCheckoutRef = useRef<HTMLElement>(null);

  const categories = useMemo(() => {
    if (!menu) return [];
    const validCategories = (menu.categories ?? []).filter((category) => {
      return menu.products.some(
        (product) =>
          shouldShowProduct(product) &&
          product.product_category?.some((pc) => pc.category_id === category.id),
      );
    });
    return sortCategories(validCategories);
  }, [menu]);

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<MenuSnapshotProduct | null>(null);
  const [editingCartItemId, setEditingCartItemId] = useState<string | null>(
    null,
  );
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [flyAnimation, setFlyAnimation] = useState<FlyToCartPayload | null>(
    null,
  );
  const [orderPanelOpen, setOrderPanelOpen] = useState(false);

  const [orderOptionsOpen, setOrderOptionsOpen] = useState(false);
  const [orderType, setOrderType] = useState<"takeaway" | "eat_here" | null>(
    null,
  );
  const [pickupMethod, setPickupMethod] = useState<
    "banderin" | "beeper" | "nombre" | null
  >(null);
  const [beeperNumber, setBeeperNumber] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleSetActiveCategoryId = useCallback((id: string) => {
    setActiveCategoryId(id);
    setSearchQuery("");
  }, []);

  const prevItemCountRef = useRef(cart.itemCount);

  useEffect(() => {
    // Si agregamos items nuevos (la cantidad total de productos crece o pasa a ser > 0)
    if (cart.itemCount > prevItemCountRef.current) {
      setOrderPanelOpen(true);
    } else if (cart.itemCount === 0) {
      // Auto cerrar si nos quedamos sin items
      setOrderPanelOpen(false);
    }
    prevItemCountRef.current = cart.itemCount;
  }, [cart.itemCount]);

  useEffect(() => {
    // Si no hay orderType seleccionado, mostramos el overlay automáticamente al iniciar
    if (!orderType) {
      setOrderOptionsOpen(true);
    }
  }, [orderType]);

  useEffect(() => {
    const handleReset = () => {
      setOrderType(null);
      setPickupMethod(null);
      setBeeperNumber("");
      setOrderOptionsOpen(true);
      // Opcional: Si quisieras vaciar el carrito, puedes descomentar la siguiente línea
      // cart.clearCart();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("reset-totem-order", handleReset);
      return () => window.removeEventListener("reset-totem-order", handleReset);
    }
  }, []);

  const resolvedCategoryId = useMemo(() => {
    if (activeCategoryId && categories.some((c) => c.id === activeCategoryId)) {
      return activeCategoryId;
    }
    return categories[0]?.id ?? null;
  }, [activeCategoryId, categories]);

  const activeCategoryName = useMemo(() => {
    return categories.find((c) => c.id === resolvedCategoryId)?.name ?? "";
  }, [categories, resolvedCategoryId]);

  const triggerFlyToCart = useCallback((payload: FlyToCartPayload) => {
    setFlyAnimation(payload);
  }, []);

  const clearFlyAnimation = useCallback(() => {
    setFlyAnimation(null);
  }, []);

  const value = useMemo<TotemContextValue>(
    () => ({
      totem,
      totemLoading,
      menu,
      menuLoading,
      categories,
      activeCategoryId: resolvedCategoryId,
      setActiveCategoryId: handleSetActiveCategoryId,
      activeCategoryName,
      cart,
      selectedProduct,
      setSelectedProduct,
      editingCartItemId,
      setEditingCartItemId,
      checkoutOpen,
      setCheckoutOpen,
      triggerFlyToCart,
      flyAnimation,
      clearFlyAnimation,
      desktopCheckoutRef,
      mobileCheckoutRef,
      orderPanelOpen,
      setOrderPanelOpen,
      orderOptionsOpen,
      setOrderOptionsOpen,
      orderType,
      setOrderType,
      pickupMethod,
      setPickupMethod,
      beeperNumber,
      setBeeperNumber,
      searchQuery,
      setSearchQuery,
    }),
    [
      totem,
      totemLoading,
      menu,
      menuLoading,
      categories,
      resolvedCategoryId,
      activeCategoryName,
      handleSetActiveCategoryId,
      cart,
      selectedProduct,
      editingCartItemId,
      checkoutOpen,
      triggerFlyToCart,
      flyAnimation,
      clearFlyAnimation,
      orderPanelOpen,
      orderOptionsOpen,
      orderType,
      pickupMethod,
      beeperNumber,
      searchQuery,
    ],
  );

  return <TotemCtx.Provider value={value}>{children}</TotemCtx.Provider>;
}
